import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { isAllowedWhileLocked, isFormationLocked } from "@/lib/formationLock";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [roleChecked, setRoleChecked] = useState(false);
  const [isSuccessor, setIsSuccessor] = useState(false);
  const [formationLocked, setFormationLocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) { setRoleChecked(true); return; }
      const [{ data }, { data: record }] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", user.id),
        (supabase as any)
          .from("member_records")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const roles = (data || []).map((r: any) => r.role);
      // If they only have 'successor' (or successor is present alone), lock them to /successor
      setIsSuccessor(roles.length > 0 && roles.every((r: string) => r === "successor"));
      setFormationLocked(isFormationLocked(record));
      setRoleChecked(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  if (loading || (user && !roleChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  if (isSuccessor && location.pathname !== "/successor") {
    return <Navigate to="/successor" replace />;
  }

  // Formation mode: the tracking page is their only room until documents arrive.
  if (formationLocked && !isAllowedWhileLocked(location.pathname)) {
    return <Navigate to="/tracking" replace />;
  }

  return <>{children}</>;
};


export default ProtectedRoute;
